import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Plus, RotateCcw, Pencil, AlertTriangle, X, Trash2 , LayoutGrid, List } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import ThemeToggle from '../../../components/ThemeToggle';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';

export default function DeviceTypes() {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, deviceType: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, deviceType: null });
  const [editFormData, setEditFormData] = useState({
    name: '',
    modelName: '',
    manufacturer: '',
    firmwareVersion: '',
    hardwareVersion: '',
    description: '',
    status: 'active'
  });
  const { get, put, delete: deleteApi } = useApi();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  useEffect(() => {
    fetchDeviceTypes();
  }, [currentPage]);

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? 'grid' : 'table');
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const fetchDeviceTypes = async () => {
    try {
      setIsLoading(true);
      const response = await get(`/device-types?page=${currentPage}&perPage=${itemsPerPage}&sortBy=manufacturer&sortOrder=asc`);
      
      if (response?.success && Array.isArray(response?.data)) {
        setDeviceTypes(response.data);
        if (response.meta) {
          setTotalPages(response.meta.lastPage);
          setTotalItems(response.meta.total);
        }
      } else {
        setDeviceTypes([]);
      }
    } catch (error) {
      toast.error('Failed to fetch device types');
      setDeviceTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDeviceTypes();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const openDeleteDialog = (deviceType) => {
    setDeleteDialog({ isOpen: true, deviceType });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, deviceType: null });
  };

  const openEditDialog = (deviceType) => {
    setEditDialog({ isOpen: true, deviceType });
    setEditFormData({
      name: deviceType.name || '',
      modelName: deviceType.modelName || '',
      manufacturer: deviceType.manufacturer || '',
      firmwareVersion: deviceType.firmwareVersion || '',
      hardwareVersion: deviceType.hardwareVersion || '',
      description: deviceType.description || '',
      status: deviceType.status || 'active'
    });
  };

  const closeEditDialog = () => {
    setEditDialog({ isOpen: false, deviceType: null });
    setEditFormData({
      name: '',
      modelName: '',
      manufacturer: '',
      firmwareVersion: '',
      hardwareVersion: '',
      description: '',
      status: 'active'
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateDeviceType = async () => {
    if (!editDialog.deviceType) return;
    
    try {
      const apiPayload = {
        name: editFormData.name,
        model_name: editFormData.modelName,
        manufacturer: editFormData.manufacturer,
        firmware_version: editFormData.firmwareVersion,
        hardware_version: editFormData.hardwareVersion,
        description: editFormData.description,
        status: editFormData.status
      };
      
      const response = await put(`/device-types/${editDialog.deviceType._id}`, apiPayload);
      
      setDeviceTypes(prevDeviceTypes => 
        prevDeviceTypes.map(deviceType => 
          deviceType._id === editDialog.deviceType._id 
            ? { ...deviceType, ...editFormData }
            : deviceType
        )
      );
      
      toast.success('Device type updated successfully');
      closeEditDialog();
    } catch (error) {
      toast.error('Failed to update device type');
    }
  };

  const handleDeleteDeviceType = async () => {
    if (!deleteDialog.deviceType) return;
    
    try {
      await deleteApi(`/device-types/${deleteDialog.deviceType._id}`);
      
      setDeviceTypes(prevDeviceTypes => 
        prevDeviceTypes.filter(deviceType => deviceType._id !== deleteDialog.deviceType._id)
      );
      
      toast.success('Device type deleted successfully');
      closeDeleteDialog();
      fetchDeviceTypes();
    } catch (error) {
      toast.error('Failed to delete device type');
    }
  };

  const filteredDeviceTypes = deviceTypes.filter(deviceType =>
    deviceType.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deviceType.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deviceType.modelName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold">Device Types</h1>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
         
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            {viewMode === 'table' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            <span className="hidden sm:inline">{viewMode === 'table' ? 'Grid' : 'Table'}</span>
          </Button>
          <Link to="/admin/device-types/add">
            
          </Link>
        </div>
      </div>

      {/* Table or Grid View */}
      {viewMode === 'table' ? (
      <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
        <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
          <TableHeader className="bg-muted/70 border-1 border-muted-foreground/20">
            <TableRow className="h-8 bg-muted/50">
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px]">Date</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px]">Name</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px] hidden md:table-cell">Model</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px]">Manufacturer</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px] hidden lg:table-cell">Firmware</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px] hidden lg:table-cell">Hardware</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[70px] sm:min-w-[100px]">Status</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[70px] sm:min-w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDeviceTypes.map((deviceType) => (
              <TableRow key={deviceType._id} className="h-8">
                <TableCell className="px-2 sm:px-3 py-2">
                  <span className="text-xs sm:text-sm ">
                    {new Date(deviceType.createdAt).toLocaleDateString('en-GB')}
                  </span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2">
                  <span className="text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[120px]">{deviceType.name}</span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
                  <span className="text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[100px]">{deviceType.modelName}</span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2">
                  <span className="text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[120px]">{deviceType.manufacturer}</span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
                  <span className="text-xs sm:text-sm ">{deviceType.firmwareVersion}</span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
                  <span className="text-xs sm:text-sm ">{deviceType.hardwareVersion}</span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2">
                  <span className={`px-1 py-0.5 rounded text-xs sm:text-sm font-medium ${
                    deviceType.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                    deviceType.status === 'inactive' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                  }`}>
                    {deviceType.status || 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="px-2 sm:px-3 py-2">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openEditDialog(deviceType)}
                      className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                    >
                      <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openDeleteDialog(deviceType)}
                      className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            /* Skeleton Grid */
            Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
                <div className="p-4 space-y-3">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
              </div>
            ))
          ) : filteredDeviceTypes.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <X className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No device types found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first device type'}
              </p>
            </div>
          ) : (
            filteredDeviceTypes.map((deviceType) => (
              <div 
                key={deviceType._id} 
                className="bg-card text-card-foreground rounded-sm overflow-hidden border-1 border-muted-foreground/20 hover:shadow-lg transition-all duration-300 hover:border-primary/20 group bg-muted/50"
              >
                <div className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">Device Name</p>
                      <p className="text-sm font-semibold truncate">{deviceType.name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(deviceType)}
                        className="h-8 w-8 p-0"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openDeleteDialog(deviceType)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Model Name */}
                  {deviceType.modelName && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Model</p>
                      <p className="text-sm truncate">{deviceType.modelName}</p>
                    </div>
                  )}

                  {/* Manufacturer */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Manufacturer</p>
                    <p className="text-sm font-medium truncate">{deviceType.manufacturer || 'N/A'}</p>
                  </div>

                  {/* Firmware Version */}
                  {deviceType.firmwareVersion && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Firmware</p>
                      <p className="text-sm truncate">{deviceType.firmwareVersion}</p>
                    </div>
                  )}

                  {/* Hardware Version */}
                  {deviceType.hardwareVersion && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Hardware</p>
                      <p className="text-sm truncate">{deviceType.hardwareVersion}</p>
                    </div>
                  )}

                  {/* Date & Status */}
                  <div className="flex items-center justify-between pt-2 border-t border-muted-foreground/10">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(deviceType.createdAt).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      deviceType.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      deviceType.status === 'inactive' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                    }`}>
                      {deviceType.status || 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
        <div className="text-sm text-muted-foreground">
          Showing page {currentPage} of {totalPages} | Total: {totalItems} device types
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="hover:bg-muted/50"
          >
            Previous
          </Button>
          {/* Show page 1 if not in range */}
          {currentPage > 2 && totalPages > 3 && (
            <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(1)}
                className={`${
                  currentPage === 1 
                    ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                    : 'text-foreground hover:bg-muted/50'
                }`}
          >
            1
          </Button>
              {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
            </>
          )}
          {/* Show pages around current page */}
          {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
            let page;
            if (totalPages <= 5) {
              page = i + 1;
            } else if (currentPage <= 3) {
              page = i + 1;
            } else if (currentPage >= totalPages - 2) {
              page = totalPages - 4 + i;
            } else {
              page = currentPage - 2 + i;
            }
            return page;
          })
          .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
          .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
          .map(page => (
            <Button
              key={page}
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page)}
              className={`${
                currentPage === page 
                  ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                  : 'text-foreground hover:bg-muted/50'
              }`}
            >
              {page}
            </Button>
          ))}
          {/* Show last page if not in range */}
          {currentPage < totalPages - 2 && totalPages > 3 && (
            <>
              {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
            <Button
              variant="outline"
              size="sm"
                onClick={() => handlePageChange(totalPages)}
                className={`${
                  currentPage === totalPages 
                    ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                    : 'text-foreground hover:bg-muted/50'
                }`}
            >
                {totalPages}
            </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="hover:bg-muted/50"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Delete Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Device Type</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete "{deleteDialog.deviceType?.name}"?
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={closeDeleteDialog}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteDeviceType}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Device Type Sheet */}
      <Sheet open={editDialog.isOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Pencil className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Edit Device Type
            </SheetTitle>
            <SheetDescription>
              Update device type information
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Device Name</label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  placeholder="Enter device name"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Model Name</label>
                <Input
                  value={editFormData.modelName}
                  onChange={(e) => handleEditFormChange('modelName', e.target.value)}
                  placeholder="Enter model name"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Manufacturer</label>
                <Input
                  value={editFormData.manufacturer}
                  onChange={(e) => handleEditFormChange('manufacturer', e.target.value)}
                  placeholder="Enter manufacturer"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Firmware Version</label>
                <Input
                  value={editFormData.firmwareVersion}
                  onChange={(e) => handleEditFormChange('firmwareVersion', e.target.value)}
                  placeholder="Enter firmware version"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Hardware Version</label>
                <Input
                  value={editFormData.hardwareVersion}
                  onChange={(e) => handleEditFormChange('hardwareVersion', e.target.value)}
                  placeholder="Enter hardware version"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-muted-foreground bg-muted/50 border-1 border-muted-foreground/20 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-3 py-2 rounded-md text-muted-foreground bg-muted/50 border-1 border-muted-foreground/20 focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end p-6 pt-4 border-t border-border/20 bg-background">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              className="px-4 py-2 text-muted-foreground border-1 border-muted-foreground/20"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUpdateDeviceType}
              className="px-4 py-2 text-muted-foreground border-1 border-muted-foreground/20"
            >
              Update Device Type
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
